import { useEffect } from 'react';
import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconChevronRight,
  IconGauge,
  IconLogout,
  IconMoon,
  IconNotebook,
  IconPlaneTilt,
  IconSettings,
  IconShieldCheck,
  IconSun,
  IconTool,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { OrganisationSwitcher } from '../components/OrganisationSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { useSession } from './session';

type NavItem = {
  to: string;
  label: string;
  icon: typeof IconGauge;
  permissions?: string[];
  badge?: number;
  badgeColor?: string;
};

export function AppLayout() {
  const { t } = useTranslation();
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const { organisation, organisations, user, can, signOut, selectOrganisation } = useSession();
  const params = useParams({ strict: false }) as { org?: string };
  const navigate = useNavigate();
  const location = useLocation();
  const { setColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme('light');

  // The URL is the source of truth for which club is being viewed, so a shared link opens the
  // right one even if the recipient was last working somewhere else.
  useEffect(() => {
    const fromUrl = params.org;
    if (!fromUrl || !organisation || fromUrl === organisation.slug) return;
    if (organisations.some((entry) => entry.slug === fromUrl)) {
      void selectOrganisation(fromUrl);
    } else {
      void navigate({ to: '/$org/dashboard', params: { org: organisation.slug }, replace: true });
    }
  }, [params.org, organisation, organisations, selectOrganisation, navigate]);

  const alertSummary = useQuery({
    queryKey: ['alerts', 'summary', organisation?.slug],
    queryFn: async () => unwrap(await api.GET('/api/v1/alerts/events/summary/')),
    enabled: Boolean(organisation) && can('alerts.view'),
    refetchInterval: 5 * 60 * 1000,
  });

  const org = organisation?.slug ?? '';

  const items: NavItem[] = [
    { to: `/${org}/dashboard`, label: t('nav.dashboard'), icon: IconGauge },
    {
      to: `/${org}/organisation`,
      label: t('nav.organisation'),
      icon: IconBuilding,
      permissions: ['organisation.view'],
    },
    { to: `/${org}/staff`, label: t('nav.staff'), icon: IconUsers, permissions: ['staff.view'] },
    {
      to: `/${org}/aircraft`,
      label: t('nav.aircraft'),
      icon: IconPlaneTilt,
      permissions: ['aircraft.view'],
    },
    {
      to: `/${org}/logbook`,
      label: t('nav.logbook'),
      icon: IconNotebook,
      permissions: ['logbook.view'],
    },
    {
      to: `/${org}/maintenance`,
      label: t('nav.maintenance'),
      icon: IconTool,
      permissions: ['maintenance.view'],
    },
    {
      to: `/${org}/alerts`,
      label: t('nav.alerts'),
      icon: IconAlertTriangle,
      permissions: ['alerts.view'],
      badge: alertSummary.data?.unacknowledged,
      badgeColor: alertSummary.data?.critical ? 'red' : 'orange',
    },
    {
      to: `/${org}/settings`,
      label: t('nav.settings'),
      icon: IconSettings,
      permissions: ['taxonomy.view'],
    },
  ];

  const visible = items.filter((item) => !item.permissions || can(...item.permissions));

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: !mobileOpen } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={mobileOpen} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Group gap={8} wrap="nowrap">
              <IconPlaneTilt size={20} />
              <Text fw={700} fz="lg" visibleFrom="xs">
                {t('app.name')}
              </Text>
            </Group>
          </Group>

          <Group gap={4} wrap="nowrap">
            <NotificationBell />
            <LanguageSwitcher />
            <Tooltip label={t('common.theme')}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                onClick={() => setColorScheme(colorScheme === 'dark' ? 'light' : 'dark')}
                aria-label={t('common.theme')}
              >
                {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>

            <Menu position="bottom-end" withinPortal shadow="md" width={220}>
              <Menu.Target>
                <UnstyledButton px={6}>
                  <Group gap={6} wrap="nowrap">
                    <IconUserCircle size={22} />
                    <Text fz="sm" visibleFrom="sm" truncate="end" maw={160}>
                      {user?.person?.full_name || user?.full_name || user?.email}
                    </Text>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user?.email}</Menu.Label>
                <Menu.Item
                  leftSection={<IconUserCircle size={15} />}
                  component={Link}
                  to={`/${org}/profile`}
                >
                  {t('settings.myProfile')}
                </Menu.Item>
                {user?.is_staff ? (
                  <Menu.Item
                    leftSection={<IconShieldCheck size={15} />}
                    component="a"
                    href="/admin/"
                  >
                    {t('nav.admin')}
                  </Menu.Item>
                ) : null}
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={15} />}
                  onClick={() => void signOut()}
                >
                  {t('action.signOut')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <AppShell.Section mb="sm">
          <OrganisationSwitcher />
        </AppShell.Section>
        <AppShell.Section grow component={ScrollArea} type="auto">
          <Stack gap={2}>
            {visible.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  component={Link}
                  to={item.to}
                  onClick={closeMobile}
                  active={isActive}
                  label={item.label}
                  leftSection={<Icon size={18} />}
                  rightSection={
                    item.badge ? (
                      <Badge size="sm" circle color={item.badgeColor}>
                        {item.badge}
                      </Badge>
                    ) : isActive ? (
                      <IconChevronRight size={14} opacity={0.5} />
                    ) : null
                  }
                  variant="light"
                  style={{ borderRadius: 'var(--mantine-radius-md)' }}
                />
              );
            })}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <Divider mb="xs" />
          <Group gap={6} px="xs" pb={4}>
            <IconBell size={13} opacity={0.5} />
            <Text fz="xs" c="dimmed">
              {t('app.tagline')}
            </Text>
          </Group>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
